import { createClient } from '@supabase/supabase-js';

// Supabase configuration
const supabaseUrl = 'https://ygyvkdaeqvbrvugpwsoa.supabase.co';
const supabaseAnonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InlneXZrZGFlcXZicnZ1Z3B3c29hIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDc2MDI3MzQsImV4cCI6MjA2MzE3ODczNH0.yz5nqn2hpgF78q3qIxxLvJB9I6bTDwBztOTtsWKwCj0';

// Create Supabase client with additional options
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  }
});

/**
 * Initialize anonymous session with Supabase
 * @returns {Promise} Promise that resolves when auth is complete
 */
export const initializeAnonAuth = async () => {
  try {
    console.log('Starting anonymous authentication...');
    
    // Try to get the current session first
    let { data, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      console.error('Error getting session:', sessionError);
      throw sessionError;
    }
    
    // Check if we have a valid session
    if (!data?.session) {
      console.log('No existing session found, signing in anonymously...');
      
      // Sign in anonymously
      const { data: signInData, error: signInError } = await supabase.auth.signInAnonymously();
      
      if (signInError) {
        console.error('Error signing in anonymously:', signInError);
        throw signInError;
      }
      
      if (!signInData?.session) {
        throw new Error('Failed to create anonymous session');
      }
      
      console.log('Anonymous authentication successful', signInData.user.id);
      return { success: true, session: signInData.session };
    } else {
      console.log('Using existing session');
      return { success: true, session: data.session };
    }
  } catch (error) {
    console.error('Error initializing anonymous auth:', error);
    return { success: false, error };
  }
};

/**
 * Fetch Nova configuration from edge function
 * @param {string} slug - The slug identifier for the configuration
 * @param {Object} session - Optional session object from initializeAnonAuth
 * @returns {Promise} Promise that resolves with the configuration
 */
export const fetchNovaConfig = async (slug, session = null) => {
  try {
    console.log('Fetching Nova config for slug:', slug);
    
    // Get JWT either from provided session or get a new session
    let jwt;
    
    if (session && session.access_token) {
      jwt = session.access_token;
      console.log('Using provided session for authentication');
    } else {
      console.log('No session provided, getting current session');
      // Try to initialize auth first to ensure we have a valid session
      const authResult = await initializeAnonAuth();
      
      if (!authResult.success || !authResult.session?.access_token) {
        throw new Error('Could not obtain a valid authentication token');
      }
      
      jwt = authResult.session.access_token;
      console.log('Successfully obtained authentication token');
    }
    
    // Direct fetch to edge function endpoint with slug in request body
    console.log('Making request to edge function...');
    const response = await fetch('https://ygyvkdaeqvbrvugpwsoa.supabase.co/functions/v1/get-nova-config', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${jwt}`
      },
      body: JSON.stringify({ slug })
    });
    
    if (!response.ok) {
      throw new Error(`Edge function returned ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    console.error('Error fetching Nova config:', error);
    return { success: false, error };
  }
};

/**
 * Submit a vote to the Supabase edge function
 * @param {Object} voteData - The vote data to submit
 * @param {Object} session - The authenticated session
 * @returns {Promise<Object>} - The response from the edge function
 */
export const submitVote = async (voteData, session) => {
  try {
    console.log('Submitting vote:', voteData);
    
    if (!session?.access_token) {
      console.error('No access token available for vote submission');
      return { success: false, error: 'Authentication required' };
    }
    
    // Debug: Print token info (first 10 chars only for security)
    console.log('Token available:', session.access_token ? `${session.access_token.substring(0, 10)}...` : 'none');
    console.log('User ID:', session.user?.id || 'anonymous');
    console.log('Token expires at:', new Date(session.expires_at * 1000).toLocaleString());
    
    // Debug: Print request details before making the request
    console.log('Vote submission request:', {
      url: 'https://ygyvkdaeqvbrvugpwsoa.supabase.co/functions/v1/add-vote',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token.substring(0, 10)}...`
      },
      body: {
        poll_id: voteData.pollId,
        user_email: voteData.email,
        first_name: voteData.firstName,
        last_name: voteData.lastName,
        contestant_id: voteData.contestantId,
        session_id: session.user?.id || null,
        turnstile_token: voteData.turnstileToken || null,
      }
    });
    
    // Make the actual request
    const response = await fetch(
      'https://ygyvkdaeqvbrvugpwsoa.supabase.co/functions/v1/add-vote',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          poll_id: voteData.pollId,
          user_email: voteData.email,
          first_name: voteData.firstName,
          last_name: voteData.lastName,
          contestant_id: voteData.contestantId,
          session_id: session.user?.id || null,
          turnstile_token: voteData.turnstileToken || null,
        })
      }
    );
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Vote submission failed:', response.status, errorData);
      console.error('Response headers:', [...response.headers.entries()]);
      console.error('Response status:', response.status, response.statusText);
      return { 
        success: false, 
        error: errorData.message || `Error: ${response.status} - ${response.statusText}`,
        status: response.status
      };
    }
    
    const data = await response.json();
    console.log('Vote submission successful:', data);
    return { success: true, data };
  } catch (error) {
    console.error('Error submitting vote:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Fetch site configuration from Supabase
 * @returns {Promise<Object>} The site configuration
 */
export const fetchSiteConfig = async () => {
  try {
    const { data, error } = await supabase
      .from('site_config_by_app')
      .select('config')
      .eq('app_id', 'com.inkifylabs.toon-house-voting')
      .single();

    if (error) {
      console.error('Error fetching site config:', error);
      return { success: false, error };
    }

    return { success: true, data: data.config };
  } catch (error) {
    console.error('Error in fetchSiteConfig:', error);
    return { success: false, error };
  }
};

/**
 * Fetch the currently active poll from Supabase
 * @returns {Promise<Object>} The active poll configuration
 */
export const fetchActivePoll = async (session = null) => {
  try {
    console.log('Fetching active poll...');
    
    // Get JWT either from provided session or get a new session
    let jwt;
    
    if (session && session.access_token) {
      jwt = session.access_token;
      console.log('Using provided session for authentication');
    } else {
      console.log('No session provided, getting current session');
      // Try to initialize auth first to ensure we have a valid session
      const authResult = await initializeAnonAuth();
      
      if (!authResult.success || !authResult.session?.access_token) {
        throw new Error('Could not obtain a valid authentication token');
      }
      
      jwt = authResult.session.access_token;
      console.log('Successfully obtained authentication token');
    }
    
    // Direct fetch to edge function endpoint to get active poll
    console.log('Making request to edge function for active poll...');
    const response = await fetch('https://ygyvkdaeqvbrvugpwsoa.supabase.co/functions/v1/get-active-poll', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${jwt}`
      }
    });
    
    if (!response.ok) {
      throw new Error(`Edge function returned ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    console.error('Error fetching active poll:', error);
    return { success: false, error };
  }
};
